import api from './api';

const BASE_URL = '/users';

export const settingsService = {
    // Core settings
    async getCurrentUser() {
        const response = await api.get('/auth/me');
        return response.data;
    },

    async getUserSettings() {
        const userId = (await this.getCurrentUser()).id;
        const response = await api.get(`${BASE_URL}/${userId}/settings`);
        return response.data;
    },

    async updateUserSettings(settings) {
        const userId = (await this.getCurrentUser()).id;
        const response = await api.put(`${BASE_URL}/${userId}/settings`, settings);
        return response.data;
    },

    // Platforms available for social links: { platforms: [{ key, displayName, inputType, iconSlug, urlPrefix }], maxLinks }
    async getSocialPlatforms() {
        const response = await api.get('/social-platforms');
        return response.data;
    },

    // Privacy Settings
    async getPrivacySettings() {
        const userId = (await this.getCurrentUser()).id;
        const response = await api.get(`${BASE_URL}/${userId}/privacy-settings`);
        return response.data;
    },

    async updatePrivacySettings(settings) {
        const userId = (await this.getCurrentUser()).id;
        const response = await api.put(`${BASE_URL}/${userId}/privacy-settings`, settings);
        return response.data;
    },

    // Content Settings
    async getContentSettings() {
        const userId = (await this.getCurrentUser()).id;
        const response = await api.get(`${BASE_URL}/${userId}/content-monetization-settings`);
        return response.data;
    },

    async updateContentSettings(settings) {
        const userId = (await this.getCurrentUser()).id;
        const response = await api.put(`${BASE_URL}/${userId}/content-monetization-settings`, settings);
        return response.data;
    },

    // Subscription Plans
    async getSubscriptionPlans(userId) {
        if (!userId) {
            userId = (await this.getCurrentUser()).id;
        }
        const response = await api.get(`${BASE_URL}/${userId}/subscription-plans`);
        return response.data;
    },

    async createSubscriptionPlan(planData) {
        const userId = (await this.getCurrentUser()).id;
        const cleanPlan = this.cleanPlanData(planData);
        try {
            const response = await api.post(`${BASE_URL}/${userId}/subscription-plans`, cleanPlan);
            return response.data;
        } catch (error) {
            console.error('Failed to create subscription plan:', error);
            throw error;
        }
    },

    async updateSubscriptionPlan(planId, planData) {
        const userId = (await this.getCurrentUser()).id;
        const cleanPlan = this.cleanPlanData(planData);
        const response = await api.put(`${BASE_URL}/${userId}/subscription-plans/${planId}`, cleanPlan);
        return response.data;
    },

    async deleteSubscriptionPlan(planId) {
        const userId = (await this.getCurrentUser()).id;
        await api.delete(`${BASE_URL}/${userId}/subscription-plans/${planId}`);
    },

    async updateSubscriptionPlans({ plans }) {
        
        if (!Array.isArray(plans)) {
            console.error('Invalid plans data structure:', plans);
            throw new Error('Invalid plans data structure');
        }

        const updatedPlans = [];

        // Process plans in sequence to maintain order
        for (const plan of plans) {
            try {
                if (plan.id?.toString().startsWith('temp_')) {
                    // New plan
                    const newPlan = await this.createSubscriptionPlan(plan);
                    updatedPlans.push(newPlan);
                } else {
                    // Existing plan
                    const updatedPlan = await this.updateSubscriptionPlan(plan.id, plan);
                    updatedPlans.push(updatedPlan);
                }
            } catch (error) {
                console.error(`Failed to process plan ${plan.id}:`, error);
                throw error;
            }
        }
        return updatedPlans;
    },

    // Helper to clean and validate plan data
    // Single subscription per creator model - no planName/description needed
    cleanPlanData(planData) {
        const cleaned = {
            amount: parseFloat(planData.price || planData.amount),
            currency: planData.currency || 'USD',
            isActive: planData.isActive ?? true,
            // Discount percentages for multi-month tiers
            threeMonthDiscount: parseFloat(planData.threeMonthDiscount) || 0,
            sixMonthDiscount: parseFloat(planData.sixMonthDiscount) || 0,
            yearlyDiscount: parseFloat(planData.yearlyDiscount) || 0,
            // Optional fields
            trialPeriodDays: planData.trialPeriodDays || 0,
            trialAmount: planData.trialAmount || 0.00,
            maxSubscribers: planData.maxSubscribers || null
        };

        return cleaned;
    },

    // Notification Settings
    async updateNotificationSettings(settings) {
        return this.updateUserSettings({
            notifications: settings
        });
    },

    // Payout Settings
    async getPayoutSettings() {
        const userId = (await this.getCurrentUser()).id;
        const response = await api.get(`${BASE_URL}/${userId}/payout-settings`);
        return response.data;
    },

    async updatePayoutSettings(settings) {
        const userId = (await this.getCurrentUser()).id;
        const response = await api.put(`${BASE_URL}/${userId}/payout-settings`, settings);
        return response.data;
    },

    async getBlockedUsers() {
        const userId = (await this.getCurrentUser()).id;
        const response = await api.get(`${BASE_URL}/${userId}/privacy-settings/restrictions/blocked`);
        return response.data;
    },

    async getMutedUsers() {
        const userId = (await this.getCurrentUser()).id;
        const response = await api.get(`${BASE_URL}/${userId}/privacy-settings/restrictions/muted`);
        return response.data;
    },

    async blockUser(targetId, reason) {
        const userId = (await this.getCurrentUser()).id;
        const response = await api.post(`${BASE_URL}/${userId}/privacy-settings/restrictions/block/${targetId}`, null, {
            params: { reason }
        });
        return response.data;
    },

    async unblockUser(targetId) {
        const userId = (await this.getCurrentUser()).id;
        const response = await api.delete(`${BASE_URL}/${userId}/privacy-settings/restrictions/block/${targetId}`);
        return response.data;
    },

    async muteUser(targetId, durationHours = 24) {
        const userId = (await this.getCurrentUser()).id;
        const response = await api.post(`${BASE_URL}/${userId}/privacy-settings/restrictions/mute/${targetId}`, null, {
            params: { durationHours }
        });
        return response.data;
    },

    async unmuteUser(targetId) {
        if (!targetId) {
            throw new Error('Target user ID is required');
        }
        const userId = (await this.getCurrentUser()).id;
        const response = await api.delete(`${BASE_URL}/${userId}/privacy-settings/restrictions/mute/${targetId}`);
        return response.data;
    },

    async getRestrictionStatus(targetId) {
        const userId = (await this.getCurrentUser()).id;
        const response = await api.get(`${BASE_URL}/${userId}/privacy-settings/restrictions/status/${targetId}`);
        return response.data;
    },

    async deleteAccount(reason) {
        const response = await api.delete(`${BASE_URL}/me`, { data: { reason } });
        return response.data;
    }
};